import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DotMenu, { Options } from '../primitives/dotMenu/DotMenu';
import { MenuConstants } from '../../constants/MenuConstants';
import ActionDropdown from '../primitives/dropdowns/ActionDropdown';
import { checkForPermission } from '../../util/permissionUtils';
import { AppConstants } from '../../constants/AppConstants';

interface BucketMenuProps {
  className?: string;
  iconSize?: string;
  dropdownMenu?: boolean;
  onOptionClick?: Function;
  disabled?: boolean;
  hasBucketAccess?: boolean;
  isActive?: boolean;
  isFolder?: boolean;
}

const BucketMenu: React.FC<BucketMenuProps> = ({
  iconSize = '',
  className = '',
  dropdownMenu = false,
  onOptionClick,
  disabled = false,
  hasBucketAccess,
  isActive,
  isFolder = false
}) => {
  const [selectedOption, setSelectedOption] = useState<Options | null>();
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedOption) {
      switch (selectedOption.key) {
        case MenuConstants.MENU_BUCKET_VIEW_DETAILS:
          if (onOptionClick) {
            onOptionClick(MenuConstants.MENU_BUCKET_VIEW_DETAILS);
          }
          break;
        case MenuConstants.MENU_BUCKET_EDIT_DETAILS:
          if (onOptionClick) {
            onOptionClick(MenuConstants.MENU_BUCKET_EDIT_DETAILS);
          }
          break;
        case MenuConstants.MENU_BUCKET_LIST_CONTENTS:
          if (onOptionClick) {
            onOptionClick(MenuConstants.MENU_BUCKET_LIST_CONTENTS);
          }
          break;
      }
    }
  }, [selectedOption]);

  const getMenuOptions = () => {
    let options: any = [];

    if (!dropdownMenu) {
      if (checkForPermission([AppConstants.PERMISSION_AMS_GET_BUCKET]) && hasBucketAccess) {
        options.push({
          display: `${t('T_VIEW_DETAILS')}`,
          key: MenuConstants.MENU_BUCKET_VIEW_DETAILS
        });
      }
    }

    if (dropdownMenu) {
      if (checkForPermission([AppConstants.PERMISSION_AMS_UPDATE_BUCKET]) && hasBucketAccess) {
        options.push({
          display: `${t('T_BUCKET_EDIT_DETAILS')}`,
          key: MenuConstants.MENU_BUCKET_EDIT_DETAILS
        });
      }

      if (
        checkForPermission([AppConstants.PERMISSION_AMS_LIST_FILES]) &&
        hasBucketAccess &&
        isActive
      ) {
        options.push({
          display: t('T_BUCKET_FILE_FOLDER'),
          key: MenuConstants.MENU_BUCKET_LIST_CONTENTS
        });
      }
    }

    return options;
  };

  const getFolderMenuOptions = () => {
    let options: any = [];

    options.push({
      display: `${t('T_NEW_FOLDER')}`,
      key: MenuConstants.MENU_FOLDER_CREATE
    });

    if (checkForPermission([AppConstants.PERMISSION_AMS_SIGNED_URL])) {
      options.push({
        display: `${t('T_UPLOAD')}`,
        key: MenuConstants.MENU_FOLDER_UPLOAD
      });
    }

    return options;
  };

  return (
    <>
      {dropdownMenu ? (
        <ActionDropdown
          className={
            disabled
              ? 'disabled'
              : (isFolder && !getFolderMenuOptions().length) ||
                (!isFolder && !getMenuOptions()?.length)
              ? 'hidden'
              : 'cursor-not-allowed'
          }
          options={isFolder ? getFolderMenuOptions() : getMenuOptions()}
          onSelectedOptionChange={(value: any) => {
            if (isFolder && onOptionClick) {
              onOptionClick(value);
              return;
            }
            setSelectedOption(value);
          }}
          value={selectedOption}
          defaultLabel={isFolder ? t('T_MANAGE_FOLDER') : t('T_BUCKET_MANAGE')}
        />
      ) : (
        <DotMenu
          containerClassName={getMenuOptions()?.length ? '' : 'hidden cursor-not-allowed'}
          options={getMenuOptions()}
          onSelectedOptionChange={(value) => {
            setSelectedOption(value);
          }}
          value={selectedOption}
        />
      )}
    </>
  );
};

export default BucketMenu;
